import { Component } from 'react';
import ErrorModal from '../templates/ErrorModal';
import { isChunkLoadError, reloadForStaleChunks } from '../helpers/lazyWithRetry';

class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error) {
        // isChunkError is decided here, in the render phase, so the first render after the
        // crash can hold a blank frame instead of flashing a modal that a reload is about
        // to sweep away. componentDidCatch (below) owns the reload itself.
        return { hasError: true, error, isChunkError: isChunkLoadError(error) };
    }

    componentDidCatch(error, errorInfo) {
        // A code-split chunk that failed to load is a stale-build problem, not an app bug:
        // this tab is running an index.html whose asset hashes are gone from S3 (see
        // lazyWithRetry.js). Reload into the current build rather than blaming the user.
        // If the reload is declined — one was just tried, or there is no sessionStorage to
        // rate-limit it with — fall through to the modal so something is shown.
        if (isChunkLoadError(error)) {
            if (reloadForStaleChunks()) return;
            this.setState({ isChunkError: false });
        }
        console.error('ErrorBoundary caught:', error, errorInfo);
    }

    handleReload = () => {
        window.location.reload();
    };

    render() {
        if (this.state.hasError) {
            // Reload in flight (stale chunk) — render nothing rather than an error the user
            // cannot act on and that vanishes with the next document anyway.
            if (this.state.isChunkError) return null;

            // Render the provided fallback, or the shared ErrorModal — but NEVER
            // fall back to the children that just threw. Doing so makes React
            // re-render them, they throw again, getDerivedStateFromError fires
            // again, and the whole tree remounts in a tight loop, re-firing every
            // effect/API call until the server is overwhelmed.
            if (this.props.fallback) return this.props.fallback;

            // forceShow bypasses ErrorModal's beta-only gate so every user sees a
            // consistent error on a render crash. The synthesized message mirrors
            // the API-error shape ErrorModal expects; the error name lands in the
            // generic "unexpected error" case.
            return (
                <ErrorModal
                    forceShow
                    onClose={this.handleReload}
                    message={{
                        status: this.state.error?.name || 'Error',
                        url: window.location.href,
                    }}
                />
            );
        }
        return this.props.children;
    }
}

export default ErrorBoundary;
